import { ApiError, authRequest, request } from './api.js';
import { createStore } from './store.js';

/**
 * Sesja użytkownika — jedyne źródło prawdy o tym, kim jesteśmy.
 *
 * `status` rozróżnia trzy sytuacje, które inaczej zlewają się w jedną:
 *   'loading'       — jeszcze nie wiemy (pierwsze odpytanie w toku)
 *   'guest'         — wiemy, że nikt nie jest zalogowany
 *   'authenticated' — user niepusty
 *
 * Bez tego rozróżnienia router przy starcie przekierowywałby zalogowanego
 * użytkownika na logowanie, bo `user` jest jeszcze null.
 */
export const sessionStore = createStore({ status: 'loading', user: null });

/**
 * Nazwy ról po polsku — jedno źródło dla nagłówka, profilu i panelu.
 * Quizy układa wyłącznie administrator, więc osobna rola twórcy nie istnieje.
 */
export const ROLE_NAMES = {
  player: 'gracz',
  admin: 'administrator',
};

/** Czy rola użytkownika mieści się w podanym zbiorze. */
export function hasRole(user, ...roles) {
  return Boolean(user) && roles.includes(user.role);
}

/**
 * Odpytuje backend o aktualną sesję. Wywoływane przy starcie aplikacji
 * i po każdej zmianie stanu logowania.
 */
export async function refreshSession() {
  try {
    const response = await request('/me');
    sessionStore.set({ status: 'authenticated', user: response.data });
    return response.data;
  } catch (err) {
    // 401 to normalna odpowiedź dla niezalogowanego, nie awaria.
    if (err instanceof ApiError && err.status === 401) {
      sessionStore.set({ status: 'guest', user: null });
      return null;
    }
    // Backend nieosiągalny — nie udajemy, że użytkownik jest gościem,
    // ale też nie blokujemy aplikacji.
    sessionStore.set({ status: 'guest', user: null, error: err.message });
    return null;
  }
}

/** @type {Promise<unknown> | undefined} */
let initialFetch;

/**
 * Pierwsze odpytanie o sesję, uruchamiane raz przy starcie aplikacji.
 * Strażnicy tras czekają na nie, żeby nie odesłać na logowanie kogoś,
 * kto jest zalogowany — tylko jeszcze o tym nie wiemy.
 */
export function waitForSession() {
  initialFetch ??= refreshSession();
  return initialFetch;
}

export async function signIn({ email, password }) {
  await authRequest('/sign-in/email', {
    method: 'POST',
    body: { email, password },
  });
  return refreshSession();
}

export async function signOut() {
  try {
    await authRequest('/sign-out', { method: 'POST', body: {} });
  } finally {
    // Nawet gdy żądanie padnie, lokalny stan musi przestać twierdzić,
    // że jesteśmy zalogowani.
    sessionStore.set({ status: 'guest', user: null });
  }
}

export async function updateProfile(fields) {
  await authRequest('/update-user', { method: 'POST', body: fields });
  return refreshSession();
}
